import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

const TeacherDashboard = () => {
  const [userData, setUserData] = useState(null);
  const [teacherProfile, setTeacherProfile] = useState(null);
  const [assignedStudents, setAssignedStudents] = useState([]);
  const [receivedFeedback, setReceivedFeedback] = useState([]);
  const [givenFeedback, setGivenFeedback] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [feedbackDate, setFeedbackDate] = useState(new Date().toISOString().split('T')[0]);
  const navigate = useNavigate();

  useEffect(() => {
    // Get user data from localStorage
    const storedUserData = localStorage.getItem('userData');
    if (storedUserData) {
      const parsedData = JSON.parse(storedUserData);

      // Check if user is a teacher
      if (parsedData.role !== 'teacher') {
        navigate('/');
        return;
      }

      setUserData(parsedData);

      // Get teacher data from localStorage based on logged-in teacher email
      const storedTeachers = JSON.parse(localStorage.getItem('adminTeachers') || '[]');
      const storedUsers = JSON.parse(localStorage.getItem('adminUsers') || '[]');
      const currentTeacher = storedTeachers.find(t => t.email === parsedData.email);

      if (currentTeacher) {
        const teacherProfileData = {
          id: currentTeacher.id,
          name: currentTeacher.name,
          email: currentTeacher.email,
          phone: currentTeacher.phone,
          specialization: currentTeacher.specialization,
          experience: currentTeacher.experience,
          qualification: currentTeacher.qualification,
          rating: 4.8,
          totalStudents: storedUsers.filter(u => u.assignedTeacherId === currentTeacher.id).length,
          activePrograms: 3,
          joinDate: currentTeacher.joiningDate || new Date().toISOString()
        };
        setTeacherProfile(teacherProfileData);

        // Get students assigned to this teacher
        const teacherStudents = storedUsers.filter(u => u.assignedTeacherId === currentTeacher.id);
        const studentsWithDetails = teacherStudents.map(student => ({
          id: student.id,
          name: student.name,
          phone: student.phone,
          program: student.program,
          address: student.address,
          joinDate: student.joinDate,
          endDate: new Date(new Date(student.joinDate).getTime() + 90 * 24 * 60 * 60 * 1000).toISOString(), // 90 days later
          progress: Math.floor(Math.random() * 40) + 60, // Random progress 60-100
          status: student.status,
          lastClass: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(), // Random last 7 days
          attendance: Math.floor(Math.random() * 20) + 80 + '%', // Random attendance 80-100%
          assignedTeacherId: currentTeacher.id
        }));
        setAssignedStudents(studentsWithDetails);
      }


      // Mock assigned students

      // Filter students assigned to this teacher
      // (Already done above with real data)

      // Mock feedback data (feedback received from students)
      const mockFeedback = [
        {
          id: 1,
          studentName: "Rahul Singh",
          rating: 5,
          comment: "Excellent teaching method. Very professional and patient.",
          date: "2024-01-28"
        },
        {
          id: 2,
          studentName: "Amit Singh",
          rating: 5,
          comment: "Best yoga teacher! Very helpful and supportive.",
          date: "2024-02-10"
        }
      ];
      setReceivedFeedback(mockFeedback);

      // Mock feedback data (feedback given to students)
      const mockGivenFeedback = [
        {
          id: 1,
          studentName: "Anjali Patel",
          studentId: 1,
          rating: 4,
          comment: "Good progress with flexibility. Keep practicing breathing exercises.",
          date: "2024-02-15"
        },
        {
          id: 2,
          studentName: "Amit Singh",
          studentId: 4,
          rating: 3,
          comment: "Need more focus and consistency. Missing classes regularly.",
          date: "2024-02-12"
        }
      ];
      setGivenFeedback(mockGivenFeedback);
    } else {
      // Redirect to login if no user data found
      navigate('/login');
    }
  }, [navigate]);

  // Update date immediately when student selection changes
  useEffect(() => {
    if (selectedStudent && assignedStudents.length > 0) {
      const student = assignedStudents.find(s => s.id === parseInt(selectedStudent));
      if (student && student.joinDate) {
        const studentDate = student.joinDate.split('T')[0];
        setFeedbackDate(studentDate);
      }
    }
  }, [selectedStudent, assignedStudents]);


  const handleStudentChange = (studentId) => {
    setSelectedStudent(studentId);

    // Immediately auto-populate date based on selected student's join date
    if (studentId) {
      const student = assignedStudents.find(s => s.id === parseInt(studentId));
      if (student && student.joinDate) {
        const studentDate = student.joinDate.split('T')[0];
        setFeedbackDate(studentDate);
        console.log(`Selected student: ${student.name}, Date set to: ${studentDate}`);
      }
    } else {
      setFeedbackDate(new Date().toISOString().split('T')[0]);
    }
  };

  const handleSubmitFeedback = (e) => {
    e.preventDefault();

    if (!selectedStudent || !feedbackComment.trim() || !feedbackDate) {
      setFeedbackMessage('Please select a student, choose a date, and add a comment');
      return;
    }

    const student = assignedStudents.find(s => s.id === parseInt(selectedStudent));

    // Check if feedback already given to this student on this specific date
    const existingFeedback = givenFeedback.find(fb =>
      fb.studentId === parseInt(selectedStudent) && fb.date === feedbackDate
    );

    if (existingFeedback) {
      setFeedbackMessage(`❌ You have already given feedback to ${student.name} on ${new Date(feedbackDate).toLocaleDateString()}`);
      return;
    }

    const newFeedback = {
      id: givenFeedback.length + 1,
      studentName: student.name,
      studentId: student.id,
      rating: feedbackRating,
      comment: feedbackComment,
      date: feedbackDate
    };

    setGivenFeedback([...givenFeedback, newFeedback]);
    setSelectedStudent('');
    setFeedbackRating(5);
    setFeedbackComment('');
    setFeedbackDate(new Date().toISOString().split('T')[0]);
    setFeedbackMessage('✅ Feedback submitted successfully!');
    setShowSuccessPopup(true);

    // Hide popup after 5 seconds
    setTimeout(() => {
      setShowSuccessPopup(false);
    }, 5000);

    setTimeout(() => setFeedbackMessage(''), 3000);
  };

  if (!userData || !teacherProfile) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="dashboard-container">
      {/* Main Content */}
      <main className="main-content">
        <header className="dashboard-header">
          <div className="dashboard-header-content">
            {/* <img src="logo192.png" alt="Yoga Karega India" className="dashboard-logo" /> */}
            <div>
              <h1>Welcome, {teacherProfile.name}</h1>
              <p>Manage your yoga sessions and track students’ fitness & flexibility progress</p>
            </div>
            {/* <button onClick={handleLogout} className="logout-btn">
              🚪 Logout
            </button> */}
          </div>
        </header>

        {/* Teacher Profile Card */}
        <section className="dashboard-section">
          <h2>My Profile</h2>
          <div className="card-grid">
            <div className="card profile-card">
              <div className="card-content">
                <div className="profile-info">
                  <div className="info-item">
                    <label>Name:</label>
                    <span>{teacherProfile.name}</span>
                  </div>
                  <div className="info-item">
                    <label>Email:</label>
                    <span>{teacherProfile.email}</span>
                  </div>
                  <div className="info-item">
                    <label>Phone:</label>
                    <span>{teacherProfile.phone}</span>
                  </div>
                  <div className="info-item">
                    <label>Specialization:</label>
                    <span>{teacherProfile.specialization}</span>
                  </div>
                  <div className="info-item">
                    <label>Experience:</label>
                    <span>{teacherProfile.experience}</span>
                  </div>
                  <div className="info-item">
                    <label>Qualification:</label>
                    <span>{teacherProfile.qualification}</span>
                  </div>
                  <div className="info-item">
                    <label>Member Since:</label>
                    <span>{new Date(teacherProfile.joinDate).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Teacher Profile Card */}
        <section className="dashboard-section">
          <h2>My Students</h2>
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Program</th>
                  <th>Address</th>
                  <th>Status</th>
                  <th>Join Date</th>
                </tr>
              </thead>
              <tbody>
                {assignedStudents.map((student) => (
                  <tr key={student.id}>
                    <td><strong>{student.name}</strong></td>
                    <td>
                      <a
                        href={`tel:${student.phone}`}
                        className="phone-link"
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(`tel:${student.phone}`, '_self');
                        }}
                      >
                        {student.phone}
                      </a>
                    </td>
                    <td>{student.program}</td>
                    <td>{student.address}</td>
                    <td>
                      <span className={`badge ${student.status === 'Active' ? 'badge-success' : 'badge-warning'}`}>
                        {student.status}
                      </span>
                    </td>
                    <td>{new Date(student.joinDate).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Give Feedback Section */}
        <section className="dashboard-section">
          <h2>Give Feedback to Students</h2>
          <div className="card feedback-form-card">
            <div className="card-content">
              <form onSubmit={handleSubmitFeedback} className="feedback-form">
                <div className="form-group">
                  <label htmlFor="student-select">Select Student</label>
                  <select
                    id="student-select"
                    value={selectedStudent}
                    onChange={(e) => handleStudentChange(e.target.value)}
                    className="form-control"
                  >
                    <option value="">Choose a student...</option>
                    {assignedStudents.map((student) => (
                      <option key={student.id} value={student.id}>
                        {student.name} - {student.program}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="feedback-date">Date (from My Students table)</label>
                  <input
                    type="date"
                    id="feedback-date"
                    value={feedbackDate}
                    readOnly
                    className="form-control"
                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="rating-input">Rating</label>
                  <div className="rating-input">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className={`star-btn ${star <= feedbackRating ? 'active' : ''}`}
                        onClick={() => setFeedbackRating(star)}
                        title={`${star} star${star !== 1 ? 's' : ''}`}
                      >
                        ⭐
                      </button>
                    ))}
                    <span className="rating-value">({feedbackRating}/5)</span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="comment-input">Feedback Comment</label>
                  <textarea
                    id="comment-input"
                    value={feedbackComment}
                    onChange={(e) => setFeedbackComment(e.target.value)}
                    placeholder="Enter your feedback about the student's progress, areas of improvement, etc."
                    className="form-control"
                    rows="4"
                  />
                </div>

                <button type="submit" className="btn btn-primary">
                  Submit Feedback
                </button>

                {feedbackMessage && (
                  <div className={`feedback-message ${feedbackMessage.includes('✅') ? 'success' : 'error'}`}>
                    {feedbackMessage}
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>

        {/* Feedback I Gave Section */}
        <section className="dashboard-section">
          <h2>Feedback I Gave to Students</h2>
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Student Name</th>
                  <th>Rating</th>
                  <th>Feedback</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {givenFeedback.length > 0 ? (
                  givenFeedback.map(fb => (
                    <tr key={fb.id}>
                      <td><strong>{fb.studentName}</strong></td>
                      <td>
                        <span className="stars">
                          {'⭐'.repeat(fb.rating)}
                        </span>
                        <span className="rating-text">({fb.rating}/5)</span>
                      </td>
                      <td>{fb.comment}</td>
                      <td>{new Date(fb.date).toLocaleDateString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                      No feedback given yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Feedback Received from Students Section */}
        <section className="dashboard-section">
          <h2>Feedback Given by Students to Me</h2>
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Student Name</th>
                  <th>Feedback</th>
                  <th>Rating</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {receivedFeedback.length > 0 ? (
                  receivedFeedback.map(fb => (
                    <tr key={fb.id}>
                      <td><strong>{fb.studentName}</strong></td>
                      <td>{fb.comment}</td>
                      <td>
                        <span className="stars">
                          {'⭐'.repeat(fb.rating)}
                        </span>
                        <span className="rating-text">({fb.rating}/5)</span>
                      </td>
                      <td>{new Date(fb.date).toLocaleDateString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                      No feedback received from students yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Success Popup */}
        {showSuccessPopup && (
          <div className="success-popup-overlay">
            <div className="success-popup">
              <img src="https://picsum.photos/seed/yoga-success/400/300.jpg" alt="Success" className="success-image" />
              <h3>Feedback Submitted Successfully!</h3>
              <p>Your feedback has been recorded.</p>
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default TeacherDashboard;
